/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {SchemaMetadata} from '@angular/core';

import {AnimationCompiler} from '../animation/animation_compiler';
import {AnimationParser} from '../animation/animation_parser';
import {CompileDirectiveMetadata, CompileIdentifierMetadata, CompileNgModuleMetadata, CompilePipeMetadata, CompileProviderMetadata, CompileTypeSummary, createHostComponentMeta, identifierModuleUrl, identifierName} from '../compile_metadata';
import {DirectiveNormalizer} from '../directive_normalizer';
import {DirectiveWrapperCompileResult, DirectiveWrapperCompiler} from '../directive_wrapper_compiler';
import {ListWrapper} from '../facade/collection';
import {Identifiers, createIdentifier, createIdentifierToken} from '../identifiers';
import {CompileMetadataResolver} from '../metadata_resolver';
import {NgModuleCompiler} from '../ng_module_compiler';
import {OutputEmitter} from '../output/abstract_emitter';
import * as o from '../output/output_ast';
import {CompiledStylesheet, StyleCompiler} from '../style_compiler';
import {SummaryResolver} from '../summary_resolver';
import {TemplateParser} from '../template_parser/template_parser';
import {ComponentFactoryDependency, DirectiveWrapperDependency, ViewClassDependency, ViewCompileResult, ViewCompiler} from '../view_compiler/view_compiler';

import {AotCompilerHost} from './compiler_host';
import {GeneratedFile} from './generated_file';
import {StaticSymbol} from './static_symbol';
import {ResolvedStaticSymbol, StaticSymbolResolver} from './static_symbol_resolver';
import {serializeSummaries, summaryFileName} from './summary_serializer';

export class AotCompiler {
private _animationCompiler = new AnimationCompiler();
/**
 * @param {?} _host
 * @param {?} _metadataResolver
 * @param {?} _templateParser
 * @param {?} _styleCompiler
 * @param {?} _viewCompiler
 * @param {?} _dirWrapperCompiler
 * @param {?} _ngModuleCompiler
 * @param {?} _outputEmitter
 * @param {?} _summaryResolver
 * @param {?} _localeId
 * @param {?} _translationFormat
 * @param {?} _animationParser
 * @param {?} _symbolResolver
 */
constructor(
private _host: AotCompilerHost,
private _metadataResolver: CompileMetadataResolver,
private _templateParser: TemplateParser,
private _styleCompiler: StyleCompiler,
private _viewCompiler: ViewCompiler,
private _dirWrapperCompiler: DirectiveWrapperCompiler,
private _ngModuleCompiler: NgModuleCompiler,
private _outputEmitter: OutputEmitter,
private _summaryResolver: SummaryResolver<StaticSymbol>,
private _localeId: string,
private _translationFormat: string,
private _animationParser: AnimationParser,
private _symbolResolver: StaticSymbolResolver) {}
/**
 * @return {?}
 */
clearCache() { this._metadataResolver.clearCache(); }
/**
 * @param {?} rootFiles
 * @return {?}
 */
compileAll(rootFiles: string[]): Promise<GeneratedFile[]> {
    const /** @type {?} */ programSymbols = extractProgramSymbols(this._symbolResolver, rootFiles, this._host);
    const {ngModuleByPipeOrDirective, files, ngModules} =
        analyzeAndValidateNgModules(programSymbols, this._host, this._metadataResolver);
    return Promise
        .all(ngModules.map(
            ngModule => this._metadataResolver.loadNgModuleDirectiveAndPipeMetadata(
                ngModule.type.reference, false)))
        .then(() => {
          const /** @type {?} */ sourceModules = files.map(
              file => this._compileSrcFile(
                  file.srcUrl, ngModuleByPipeOrDirective, file.directives, file.pipes,
                  file.ngModules, file.injectables));
          return ListWrapper.flatten(sourceModules);
        });
  }
/**
 * @param {?} srcFileUrl
 * @param {?} ngModuleByPipeOrDirective
 * @param {?} directives
 * @param {?} pipes
 * @param {?} ngModules
 * @param {?} injectables
 * @return {?}
 */
private _compileSrcFile(
      srcFileUrl: string, ngModuleByPipeOrDirective: Map<StaticSymbol, CompileNgModuleMetadata>,
      directives: StaticSymbol[], pipes: StaticSymbol[], ngModules: StaticSymbol[],
      injectables: StaticSymbol[]): GeneratedFile[] {
    const /** @type {?} */ fileSuffix = _splitTypescriptSuffix(srcFileUrl)[1];
    const /** @type {?} */ statements: o.Statement[] = [];
    const /** @type {?} */ exportedVars: string[] = [];
    const /** @type {?} */ generatedFiles: GeneratedFile[] = [];

    generatedFiles.push(this._createSummary(srcFileUrl, directives, pipes, ngModules, injectables));

    // compile all ng modules
    exportedVars.push(
        ...ngModules.map((ngModuleType) => this._compileModule(ngModuleType, statements)));

    // compile directive wrappers
    exportedVars.push(...directives.map(
        (directiveType) => this._compileDirectiveWrapper(directiveType, statements)));

    // compile components
    directives.forEach((dirType) => {
      const /** @type {?} */ compMeta = this._metadataResolver.getDirectiveMetadata( /** @type {?} */((<any>dirType)));
      if (!compMeta.isComponent) {
        return Promise.resolve(null);
      }
      const /** @type {?} */ ngModule = ngModuleByPipeOrDirective.get(dirType);
      if (!ngModule) {
        throw new Error(
            `Internal Error: cannot determine the module for component ${identifierName(compMeta.type)}!`);
      }

      _assertComponent(compMeta);

      // compile styles
      const /** @type {?} */ stylesCompileResults = this._styleCompiler.compileComponent(compMeta);
      stylesCompileResults.externalStylesheets.forEach((compiledStyleSheet) => {
        generatedFiles.push(this._codgenStyles(srcFileUrl, compiledStyleSheet, fileSuffix));
      });

      // compile components
      exportedVars.push(
          this._compileComponentFactory(compMeta, ngModule, fileSuffix, statements),
          this._compileComponent(
              compMeta, ngModule, ngModule.transitiveModule.directives,
              stylesCompileResults.componentStylesheet, fileSuffix, statements));
    });
    if (statements.length > 0) {
      const /** @type {?} */ srcModule = this._codegenSourceModule(
          srcFileUrl, _ngfactoryModuleUrl(srcFileUrl), statements, exportedVars);
      generatedFiles.unshift(srcModule);
    }
    return generatedFiles;
  }
/**
 * @param {?} srcFileUrl
 * @param {?} directives
 * @param {?} pipes
 * @param {?} ngModules
 * @param {?} injectables
 * @return {?}
 */
private _createSummary(
      srcFileUrl: string, directives: StaticSymbol[], pipes: StaticSymbol[],
      ngModules: StaticSymbol[], injectables: StaticSymbol[]): GeneratedFile {
    const /** @type {?} */ symbolSummaries = this._symbolResolver.getSymbolsOf(srcFileUrl)
                                .map(symbol => this._symbolResolver.resolveSymbol(symbol));
    const /** @type {?} */ typeSummaries = [
      ...ngModules.map(ref => this._metadataResolver.getNgModuleSummary(ref)),
      ...directives.map(ref => this._metadataResolver.getDirectiveSummary(ref)),
      ...pipes.map(ref => this._metadataResolver.getPipeSummary(ref)),
      ...injectables.map(ref => this._metadataResolver.getInjectableSummary(ref))
    ];
    const /** @type {?} */ json = serializeSummaries(
        this._host, this._summaryResolver, this._symbolResolver, symbolSummaries, typeSummaries);
    return new GeneratedFile(srcFileUrl, summaryFileName(srcFileUrl), json);
  }
/**
 * @param {?} ngModuleType
 * @param {?} targetStatements
 * @return {?}
 */
private _compileModule(ngModuleType: StaticSymbol, targetStatements: o.Statement[]): string {
    const /** @type {?} */ ngModule = this._metadataResolver.getNgModuleMetadata(ngModuleType);
    const /** @type {?} */ providers: CompileProviderMetadata[] = [];

    if (this._localeId) {
      providers.push({
        token: createIdentifierToken(Identifiers.LOCALE_ID),
        useValue: this._localeId,
      });
    }

    if (this._translationFormat) {
      providers.push({
        token: createIdentifierToken(Identifiers.TRANSLATIONS_FORMAT),
        useValue: this._translationFormat
      });
    }

    const /** @type {?} */ appCompileResult = this._ngModuleCompiler.compile(ngModule, providers);

    appCompileResult.dependencies.forEach((dep) => {
      dep.placeholder.reference = this._symbolResolver.getStaticSymbol(
          _ngfactoryModuleUrl(identifierModuleUrl(dep.comp)), _componentFactoryName(dep.comp));
    });

    targetStatements.push(...appCompileResult.statements);
    return appCompileResult.ngModuleFactoryVar;
  }
/**
 * @param {?} directiveType
 * @param {?} targetStatements
 * @return {?}
 */
private _compileDirectiveWrapper(directiveType: StaticSymbol, targetStatements: o.Statement[]):
      string {
    const /** @type {?} */ dirMeta = this._metadataResolver.getDirectiveMetadata(directiveType);
    const /** @type {?} */ dirCompileResult = this._dirWrapperCompiler.compile(dirMeta);

    targetStatements.push(...dirCompileResult.statements);
    return dirCompileResult.dirWrapperClassVar;
  }
/**
 * @param {?} compMeta
 * @param {?} ngModule
 * @param {?} fileSuffix
 * @param {?} targetStatements
 * @return {?}
 */
private _compileComponentFactory(
      compMeta: CompileDirectiveMetadata, ngModule: CompileNgModuleMetadata, fileSuffix: string,
      targetStatements: o.Statement[]): string {
    const /** @type {?} */ hostMeta = createHostComponentMeta(
        this._symbolResolver.getStaticSymbol(
            identifierModuleUrl(compMeta.type), `${identifierName(compMeta.type)}_Host`),
        compMeta);
    const /** @type {?} */ hostViewFactoryVar = this._compileComponent(
        hostMeta, ngModule, [compMeta.type], null, fileSuffix, targetStatements);
    const /** @type {?} */ compFactoryVar = _componentFactoryName(compMeta.type);
    targetStatements.push(
        o.variable(compFactoryVar)
            .set(o.importExpr(
                      createIdentifier(Identifiers.ComponentFactory), [o.importType(compMeta.type)])
                     .instantiate(
                         [
                           o.literal(compMeta.selector),
                           o.variable(hostViewFactoryVar),
                           o.importExpr(compMeta.type),
                         ],
                         o.importType(
                             createIdentifier(Identifiers.ComponentFactory),
                             [o.importType(compMeta.type)], [o.TypeModifier.Const])))
            .toDeclStmt(null, [o.StmtModifier.Final]));
    return compFactoryVar;
  }
/**
 * @param {?} compMeta
 * @param {?} ngModule
 * @param {?} directiveIdentifiers
 * @param {?} componentStyles
 * @param {?} fileSuffix
 * @param {?} targetStatements
 * @return {?}
 */
private _compileComponent(
      compMeta: CompileDirectiveMetadata, ngModule: CompileNgModuleMetadata,
      directiveIdentifiers: CompileIdentifierMetadata[], componentStyles: CompiledStylesheet,
      fileSuffix: string, targetStatements: o.Statement[]): string {
    const /** @type {?} */ parsedAnimations = this._animationParser.parseComponent(compMeta);
    const /** @type {?} */ directives =
        directiveIdentifiers.map(dir => this._metadataResolver.getDirectiveSummary(dir.reference));
    const /** @type {?} */ pipes = ngModule.transitiveModule.pipes.map(
        pipe => this._metadataResolver.getPipeSummary(pipe.reference));

    const /** @type {?} */ parsedTemplate = this._templateParser.parse(
        compMeta, compMeta.template.template, directives, pipes, ngModule.schemas,
        identifierName(compMeta.type));
    const /** @type {?} */ stylesExpr = componentStyles ? o.variable(componentStyles.stylesVar) : o.literalArr([]);
    const /** @type {?} */ compiledAnimations =
        this._animationCompiler.compile(identifierName(compMeta.type), parsedAnimations);
    const /** @type {?} */ viewResult = this._viewCompiler.compileComponent(
        compMeta, parsedTemplate, stylesExpr, pipes, compiledAnimations);
    if (componentStyles) {
      targetStatements.push(
          ..._resolveStyleStatements(this._symbolResolver, componentStyles, fileSuffix));
    }
    compiledAnimations.forEach(entry => targetStatements.push(...entry.statements));
    targetStatements.push(..._resolveViewStatements(this._symbolResolver, viewResult));
    return viewResult.viewClassVar;
  }
/**
 * @param {?} fileUrl
 * @param {?} stylesCompileResult
 * @param {?} fileSuffix
 * @return {?}
 */
private _codgenStyles(
      fileUrl: string, stylesCompileResult: CompiledStylesheet, fileSuffix: string): GeneratedFile {
    _resolveStyleStatements(this._symbolResolver, stylesCompileResult, fileSuffix);
    return this._codegenSourceModule(
        fileUrl, _stylesModuleUrl(
                     stylesCompileResult.meta.moduleUrl, stylesCompileResult.isShimmed, fileSuffix),
        stylesCompileResult.statements, [stylesCompileResult.stylesVar]);
  }
/**
 * @param {?} srcFileUrl
 * @param {?} genFileUrl
 * @param {?} statements
 * @param {?} exportedVars
 * @return {?}
 */
private _codegenSourceModule(
      srcFileUrl: string, genFileUrl: string, statements: o.Statement[],
      exportedVars: string[]): GeneratedFile {
    return new GeneratedFile(
        srcFileUrl, genFileUrl,
        this._outputEmitter.emitStatements(genFileUrl, statements, exportedVars));
  }
}

function AotCompiler_tsickle_Closure_declarations() {
 /** @type {?} */
AotCompiler.prototype._animationCompiler;
 /** @type {?} */
AotCompiler.prototype._host;
 /** @type {?} */
AotCompiler.prototype._metadataResolver;
 /** @type {?} */
AotCompiler.prototype._templateParser;
 /** @type {?} */
AotCompiler.prototype._styleCompiler;
 /** @type {?} */
AotCompiler.prototype._viewCompiler;
 /** @type {?} */
AotCompiler.prototype._dirWrapperCompiler;
 /** @type {?} */
AotCompiler.prototype._ngModuleCompiler;
 /** @type {?} */
AotCompiler.prototype._outputEmitter;
 /** @type {?} */
AotCompiler.prototype._summaryResolver;
 /** @type {?} */
AotCompiler.prototype._localeId;
 /** @type {?} */
AotCompiler.prototype._translationFormat;
 /** @type {?} */
AotCompiler.prototype._animationParser;
 /** @type {?} */
AotCompiler.prototype._symbolResolver;
}

/**
 * @param {?} reflector
 * @param {?} compileResult
 * @return {?}
 */
function _resolveViewStatements(
    reflector: StaticSymbolResolver, compileResult: ViewCompileResult): o.Statement[] {
  compileResult.dependencies.forEach((dep) => {
    if (dep instanceof ViewClassDependency) {
      const /** @type {?} */ vfd = /** @type {?} */(( <ViewClassDependency>dep));
      vfd.placeholder.reference =
          reflector.getStaticSymbol(_ngfactoryModuleUrl(identifierModuleUrl(vfd.comp)), dep.name);
    } else if (dep instanceof ComponentFactoryDependency) {
      const /** @type {?} */ cfd = /** @type {?} */(( <ComponentFactoryDependency>dep));
      cfd.placeholder.reference = reflector.getStaticSymbol(
          _ngfactoryModuleUrl(identifierModuleUrl(cfd.comp)), _componentFactoryName(cfd.comp));
    } else if (dep instanceof DirectiveWrapperDependency) {
      const /** @type {?} */ dwd = /** @type {?} */(( <DirectiveWrapperDependency>dep));
      dwd.placeholder.reference =
          reflector.getStaticSymbol(_ngfactoryModuleUrl(identifierModuleUrl(dwd.dir)), dwd.name);
    }
  });
  return compileResult.statements;
}
/**
 * @param {?} reflector
 * @param {?} compileResult
 * @param {?} fileSuffix
 * @return {?}
 */
function _resolveStyleStatements(
    reflector: StaticSymbolResolver, compileResult: CompiledStylesheet,
    fileSuffix: string): o.Statement[] {
  compileResult.dependencies.forEach((dep) => {
    dep.valuePlaceholder.reference = reflector.getStaticSymbol(
        _stylesModuleUrl(dep.moduleUrl, dep.isShimmed, fileSuffix), dep.name);
  });
  return compileResult.statements;
}
/**
 * @param {?} dirUrl
 * @return {?}
 */
function _ngfactoryModuleUrl(dirUrl: string): string {
  const /** @type {?} */ urlWithSuffix = _splitTypescriptSuffix(dirUrl);
  return `${urlWithSuffix[0]}.ngfactory${urlWithSuffix[1]}`;
}
/**
 * @param {?} comp
 * @return {?}
 */
function _componentFactoryName(comp: CompileIdentifierMetadata): string {
  return `${identifierName(comp)}NgFactory`;
}
/**
 * @param {?} stylesheetUrl
 * @param {?} shim
 * @param {?} suffix
 * @return {?}
 */
function _stylesModuleUrl(stylesheetUrl: string, shim: boolean, suffix: string): string {
  return `${stylesheetUrl}${shim ? '.shim' : ''}.ngstyle${suffix}`;
}
/**
 * @param {?} meta
 * @return {?}
 */
function _assertComponent(meta: CompileDirectiveMetadata) {
  if (!meta.isComponent) {
    throw new Error(
        `Could not compile '${identifierName(meta.type)}' because it is not a component.`);
  }
}
/**
 * @param {?} path
 * @return {?}
 */
function _splitTypescriptSuffix(path: string): string[] {
  if (path.endsWith('.d.ts')) {
    return [path.slice(0, -5), '.ts'];
  }

  const /** @type {?} */ lastDot = path.lastIndexOf('.');

  if (lastDot !== -1) {
    return [path.substring(0, lastDot), path.substring(lastDot)];
  }

  return [path, ''];
}

export interface NgAnalyzedModules {
  ngModules: CompileNgModuleMetadata[];
  ngModuleByPipeOrDirective: Map<StaticSymbol, CompileNgModuleMetadata>;
  files: Array<{
    srcUrl: string,
    directives: StaticSymbol[],
    pipes: StaticSymbol[],
    ngModules: StaticSymbol[],
    injectables: StaticSymbol[]
  }>;
  symbolsMissingModule?: StaticSymbol[];
}

export interface NgAnalyzeModulesHost { isSourceFile(filePath: string): boolean; }
/**
 * @param {?} programStaticSymbols
 * @param {?} host
 * @param {?} metadataResolver
 * @return {?}
 */
export function analyzeNgModules(
    programStaticSymbols: StaticSymbol[], host: NgAnalyzeModulesHost,
    metadataResolver: CompileMetadataResolver): NgAnalyzedModules {
  const {ngModules, symbolsMissingModule} =
      _createNgModules(programStaticSymbols, host, metadataResolver);
  return _analyzeNgModules(programStaticSymbols, ngModules, symbolsMissingModule, metadataResolver);
}
/**
 * @param {?} programStaticSymbols
 * @param {?} host
 * @param {?} metadataResolver
 * @return {?}
 */
export function analyzeAndValidateNgModules(
    programStaticSymbols: StaticSymbol[], host: NgAnalyzeModulesHost,
    metadataResolver: CompileMetadataResolver): NgAnalyzedModules {
  const /** @type {?} */ result = analyzeNgModules(programStaticSymbols, host, metadataResolver);
  if (result.symbolsMissingModule && result.symbolsMissingModule.length) {
    const /** @type {?} */ messages = result.symbolsMissingModule.map(
        s => `Cannot determine the module for class ${s.name} in ${s.filePath}!`);
    throw new Error(messages.join('\n'));
  }
  return result;
}
/**
 * @param {?} programSymbols
 * @param {?} ngModuleMetas
 * @param {?} symbolsMissingModule
 * @param {?} metadataResolver
 * @return {?}
 */
function _analyzeNgModules(
    programSymbols: StaticSymbol[], ngModuleMetas: CompileNgModuleMetadata[],
    symbolsMissingModule: StaticSymbol[],
    metadataResolver: CompileMetadataResolver): NgAnalyzedModules {
  const /** @type {?} */ moduleMetasByRef = new Map<any, CompileNgModuleMetadata>();
  ngModuleMetas.forEach((ngModule) => moduleMetasByRef.set(ngModule.type.reference, ngModule));
  const /** @type {?} */ ngModuleByPipeOrDirective = new Map<StaticSymbol, CompileNgModuleMetadata>();
  const /** @type {?} */ ngModulesByFile = new Map<string, StaticSymbol[]>();
  const /** @type {?} */ ngDirectivesByFile = new Map<string, StaticSymbol[]>();
  const /** @type {?} */ ngPipesByFile = new Map<string, StaticSymbol[]>();
  const /** @type {?} */ ngInjectablesByFile = new Map<string, StaticSymbol[]>();
  const /** @type {?} */ filePaths = new Set<string>();

  // Make sure we produce an analyzed file for each input file
  programSymbols.forEach((symbol) => {
    const /** @type {?} */ filePath = symbol.filePath;
    filePaths.add(filePath);
    if (metadataResolver.isInjectable(symbol)) {
      ngInjectablesByFile.set(filePath, (ngInjectablesByFile.get(filePath) || []).concat(symbol));
    }
  });

  // Looping over all modules to construct:
  // - a map from file to modules `ngModulesByFile`,
  // - a map from file to directives `ngDirectivesByFile`,
  // - a map from file to pipes `ngPipesByFile`,
  // - a map from directive/pipe to module `ngModuleByPipeOrDirective`.
  ngModuleMetas.forEach((ngModuleMeta) => {
    const /** @type {?} */ srcFileUrl = ngModuleMeta.type.reference.filePath;
    filePaths.add(srcFileUrl);
    ngModulesByFile.set(
        srcFileUrl, (ngModulesByFile.get(srcFileUrl) || []).concat(ngModuleMeta.type.reference));

    ngModuleMeta.declaredDirectives.forEach((dirIdentifier) => {
      const /** @type {?} */ fileUrl = dirIdentifier.reference.filePath;
      filePaths.add(fileUrl);
      ngDirectivesByFile.set(
          fileUrl, (ngDirectivesByFile.get(fileUrl) || []).concat(dirIdentifier.reference));
      ngModuleByPipeOrDirective.set(dirIdentifier.reference, ngModuleMeta);
    });
    ngModuleMeta.declaredPipes.forEach((pipeIdentifier) => {
      const /** @type {?} */ fileUrl = pipeIdentifier.reference.filePath;
      filePaths.add(fileUrl);
      ngPipesByFile.set(
          fileUrl, (ngPipesByFile.get(fileUrl) || []).concat(pipeIdentifier.reference));
      ngModuleByPipeOrDirective.set(pipeIdentifier.reference, ngModuleMeta);
    });
  });

  const /** @type {?} */ files: {
    srcUrl: string,
    directives: StaticSymbol[],
    pipes: StaticSymbol[],
    ngModules: StaticSymbol[],
    injectables: StaticSymbol[]
  }[] = [];

  filePaths.forEach((srcUrl) => {
    const /** @type {?} */ directives = ngDirectivesByFile.get(srcUrl) || [];
    const /** @type {?} */ pipes = ngPipesByFile.get(srcUrl) || [];
    const /** @type {?} */ ngModules = ngModulesByFile.get(srcUrl) || [];
    const /** @type {?} */ injectables = ngInjectablesByFile.get(srcUrl) || [];
    files.push({srcUrl, directives, pipes, ngModules, injectables});
  });

  return {
    // map directive/pipe to module
    ngModuleByPipeOrDirective,
    // list modules and directives for every source file
    files,
    ngModules: ngModuleMetas, symbolsMissingModule
  };
}
/**
 * @param {?} staticSymbolResolver
 * @param {?} files
 * @param {?} host
 * @return {?}
 */
export function extractProgramSymbols(
    staticSymbolResolver: StaticSymbolResolver, files: string[],
    host: NgAnalyzeModulesHost): StaticSymbol[] {
  const /** @type {?} */ staticSymbols: StaticSymbol[] = [];
  files.filter(fileName => host.isSourceFile(fileName)).forEach(sourceFile => {
    staticSymbolResolver.getSymbolsOf(sourceFile).forEach((symbol) => {
      const /** @type {?} */ resolvedSymbol = staticSymbolResolver.resolveSymbol(symbol);
      const /** @type {?} */ symbolMeta = resolvedSymbol.metadata;
      if (symbolMeta) {
        if (symbolMeta.__symbolic != 'error') {
          // Ignore symbols that are only included to record error information.
          staticSymbols.push(resolvedSymbol.symbol);
        }
      }
    });
  });

  return staticSymbols;
}
/**
 * @param {?} programStaticSymbols
 * @param {?} host
 * @param {?} metadataResolver
 * @return {?}
 */
function _createNgModules(
    programStaticSymbols: StaticSymbol[], host: NgAnalyzeModulesHost,
    metadataResolver: CompileMetadataResolver):
    {ngModules: CompileNgModuleMetadata[], symbolsMissingModule: StaticSymbol[]} {
  const /** @type {?} */ ngModules = new Map<any, CompileNgModuleMetadata>();
  const /** @type {?} */ programPipesAndDirectives: StaticSymbol[] = [];
  const /** @type {?} */ ngModulePipesAndDirective = new Set<StaticSymbol>();

  const /** @type {?} */ addNgModule = (staticSymbol: any) => {
    if (ngModules.has(staticSymbol) || !host.isSourceFile(staticSymbol.filePath)) {
      return false;
    }
    const /** @type {?} */ ngModule = metadataResolver.getNgModuleMetadata(staticSymbol, false);
    if (ngModule) {
      ngModules.set(ngModule.type.reference, ngModule);
      ngModule.declaredDirectives.forEach((dir) => ngModulePipesAndDirective.add(dir.reference));
      ngModule.declaredPipes.forEach((pipe) => ngModulePipesAndDirective.add(pipe.reference));
      // For every input module add the list of transitively included modules
      ngModule.transitiveModule.modules.forEach(modMeta => addNgModule(modMeta.reference));
    }
    return !!ngModule;
  };
  programStaticSymbols.forEach((staticSymbol) => {
    if (!addNgModule(staticSymbol) &&
        (metadataResolver.isDirective(staticSymbol) || metadataResolver.isPipe(staticSymbol))) {
      programPipesAndDirectives.push(staticSymbol);
    }
  });

  // Throw an error if any of the program pipe or directives is not declared by a module
  const /** @type {?} */ symbolsMissingModule =
      programPipesAndDirectives.filter(s => !ngModulePipesAndDirective.has(s));

  return {ngModules: Array.from(ngModules.values()), symbolsMissingModule};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
